import React, { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowRight, ArrowUpRight, Sparkles, Layers, Network, Globe2, CheckCircle2,
  Cpu, GraduationCap, BarChart3, ShieldCheck, Compass, Building2, Server,
} from "lucide-react";
import { Section, Eyebrow, H1, H2, Lead, ButtonPrimary, ButtonGhost } from "../components/ui-kit";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PHASES = [
  { no: "01", icon: Compass, name: "Early Adopter Pilot", title: "The Foundation Phase",
    context: "Designed for 'Model Schools' ready to lead the transition into structured, credit-bearing digital skill development.",
    deliverables: [
      { icon: Cpu, t: "Perforange OS Alpha deployment" },
      { icon: GraduationCap, t: "Master Trainer TTT (Train the Trainer) certification" },
      { icon: ShieldCheck, t: "Baseline Student Wellness Telemetry" },
    ],
    outcome: "Proof of Concept & Institutional Branding" },
  { no: "02", icon: Network, name: "Full Infrastructure Optimization", title: "The Integration Phase",
    context: "Scaling from a single pilot to full-scale student body enrollment across your existing computer laboratory networks.",
    deliverables: [
      { icon: Layers, t: "Full Perforange OS Suite integration" },
      { icon: GraduationCap, t: "Curriculum Compliance Mapping for Grades 6–12" },
      { icon: BarChart3, t: "Advanced Performance Analytics & Career Pathing" },
    ],
    outcome: "High-Performance Digital Talent Ecosystem" },
  { no: "03", icon: Globe2, name: "State-Level Integration", title: "The National Scale Phase",
    context: "B2G and District-wide contracts for systemic impact.",
    deliverables: [
      { icon: Server, t: "Multi-lab Management Console" },
      { icon: Building2, t: "State-level data reporting for government compliance" },
      { icon: Network, t: "Integration with National Career Service (NCS) portals" },
    ],
    outcome: "Total Infrastructure Compliance" },
];

const HARDWARE_OPTIONS = [
  "Government CCL — Newly Provisioned",
  "Government CCL — Operational",
  "Private School — Existing Computer Lab",
  "Private School — Greenfield Build",
  "Hybrid / Mixed Infrastructure",
  "Not Sure — Need Audit",
];

const VOLUME_OPTIONS = [
  "Up to 100 students",
  "100 – 500 students",
  "500 – 2,000 students",
  "2,000 – 10,000 students",
  "10,000+ (District / State scale)",
];

export default function Partnership() {
  const formRef = useRef(null);
  const [form, setForm] = useState({
    organization: "", region: "", full_name: "", role: "Principal",
    email: "", phone: "", hardware_status: "", student_volume: "", message: "",
  });
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { ...form, lead_type: "proposal" });
      toast.success("Proposal request received. Our partnerships team will respond within 48 hours.");
      setForm({ organization: "", region: "", full_name: "", role: "Principal",
        email: "", phone: "", hardware_status: "", student_volume: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || "Submission failed. Please review the fields.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Section className="!pt-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-30 pointer-events-none" />
        <div className="absolute inset-0 bg-radial-orange pointer-events-none" />
        <div className="relative max-w-4xl">
          <Eyebrow><Sparkles className="w-3 h-3" /> Partnership Roadmap · 2026</Eyebrow>
          <H1>Scaling Excellence:<br />The <span className="text-gradient-orange">Perforange Partnership Roadmap</span>.</H1>
          <Lead className="mt-6 max-w-3xl">We don&apos;t just sell software; we operationalize institutional-grade mind-sports infrastructure. Join us in building the definitive ecosystem for modern digital talent.</Lead>
          <div className="mt-8 flex flex-wrap gap-3">
            <button onClick={scrollToForm} data-testid="partnership-hero-cta">
              <ButtonPrimary>Get a Proposal <ArrowUpRight className="w-4 h-4" /></ButtonPrimary>
            </button>
            <a href="#roadmap" data-testid="partnership-hero-roadmap">
              <ButtonGhost>View the Roadmap <ArrowRight className="w-4 h-4" /></ButtonGhost>
            </a>
          </div>
        </div>
      </Section>

      <Section className="!pt-0" id="roadmap">
        <div className="max-w-3xl mb-14">
          <Eyebrow>Three Phases · One Architecture</Eyebrow>
          <H2>From a single Model School to total state-level integration.</H2>
          <Lead className="mt-4">A consultative, milestone-driven path designed around the realities of Indian school infrastructure and government procurement.</Lead>
        </div>
        <div className="relative" data-testid="phase-roadmap">
          <div className="hidden lg:block absolute top-6 left-0 right-0 h-px">
            <div className="h-px bg-gradient-to-r from-transparent via-[#FF8C00]/50 to-transparent" />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {PHASES.map((p, i) => {
              const Icon = p.icon;
              return (
                <div key={p.no} data-testid={`phase-card-${i}`}
                  className="group relative rounded-2xl border border-white/8 bg-[#0C0C10] p-7 md:p-8 transition-all duration-300 hover:border-[#FF8C00]/50 hover:shadow-[0_0_60px_rgba(255,140,0,0.18)] hover:-translate-y-1">
                  <div className="absolute -top-3 left-7 hidden lg:flex items-center gap-2 px-3 py-1 rounded-full bg-[#0A0A0C] border border-white/10 group-hover:border-[#FF8C00]/50 transition-colors">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#FF8C00] shadow-[0_0_10px_#FF8C00]" />
                    <span className="text-[10px] font-mono-tech uppercase tracking-widest text-[#A1A1AA] group-hover:text-[#FF8C00] transition-colors">Phase {p.no}</span>
                  </div>
                  <div className="flex items-start justify-between">
                    <div className="w-12 h-12 rounded-xl bg-[#FF8C00]/10 border border-[#FF8C00]/30 flex items-center justify-center group-hover:bg-[#FF8C00]/20 transition-colors">
                      <Icon className="w-6 h-6 text-[#FF8C00]" />
                    </div>
                    <p className="font-display font-black text-5xl text-white/5 group-hover:text-[#FF8C00]/15 transition-colors leading-none">{p.no}</p>
                  </div>
                  <p className="mt-6 text-xs font-mono-tech uppercase tracking-widest text-[#FF8C00]">{p.name}</p>
                  <h3 className="mt-1 font-display font-black text-2xl text-white leading-tight">{p.title}</h3>
                  <p className="mt-3 text-sm text-[#A1A1AA] leading-relaxed">{p.context}</p>
                  <div className="mt-6 pt-5 border-t border-white/8">
                    <p className="text-[10px] font-mono-tech uppercase tracking-widest text-[#6a6a70] mb-3">Deliverables</p>
                    <ul className="space-y-2.5">
                      {p.deliverables.map((d, j) => {
                        const DI = d.icon;
                        return (
                          <li key={j} className="flex items-start gap-2.5 text-sm text-[#D4D4D8]">
                            <DI className="w-4 h-4 text-[#FF8C00] shrink-0 mt-0.5" />
                            <span>{d.t}</span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                  <div className="mt-6 rounded-lg border border-[#FF8C00]/20 bg-[#FF8C00]/5 p-3">
                    <p className="text-[10px] font-mono-tech uppercase tracking-widest text-[#FF8C00]">Outcome</p>
                    <p className="text-sm text-white font-bold mt-1">{p.outcome}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </Section>

      <Section className="!pt-0">
        <div className="relative overflow-hidden rounded-3xl border border-[#FF8C00]/30 bg-gradient-to-br from-[#1a1208] via-[#0A0A0C] to-[#0C0C10] p-8 md:p-12" data-testid="founder-era-box">
          <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-[#FF8C00]/15 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-[#FF8C00]/8 blur-3xl pointer-events-none" />
          <div className="relative grid md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-2 flex md:justify-center">
              <div className="relative w-20 h-20 rounded-2xl bg-[#FF8C00]/10 border border-[#FF8C00]/40 flex items-center justify-center">
                <Sparkles className="w-9 h-9 text-[#FF8C00]" />
                <div className="absolute -top-2 -right-2 px-2 py-0.5 rounded-full bg-[#FF8C00] text-[#0A0A0C] text-[9px] font-mono-tech font-black uppercase tracking-widest">Era 1</div>
              </div>
            </div>
            <div className="md:col-span-10">
              <p className="text-[11px] font-mono-tech uppercase tracking-[0.25em] text-[#FF8C00] mb-3">The Founder-Era Advantage</p>
              <p className="font-display font-bold text-xl md:text-2xl leading-relaxed text-[#E4E4E7]">
                Schools partnering during our <span className="text-white">Incubator Phase</span> are eligible for <span className="text-gradient-orange font-black">Founder-Era Pricing</span>, securing long-term discounts and priority access to new Perforange OS features before national commercial rollout.
              </p>
              <div className="mt-5 flex flex-wrap gap-2">
                {["Locked-in rates", "Priority OS rollouts", "Co-branded case study", "Direct line to founders"].map((c) => (
                  <span key={c} className="text-[10px] font-mono-tech uppercase tracking-widest px-2.5 py-1.5 rounded-md border border-[#FF8C00]/30 text-[#FFB05E] bg-[#FF8C00]/5">{c}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </Section>

      <Section className="!pt-0" ref={formRef}>
        <div className="grid md:grid-cols-12 gap-8">
          <div className="md:col-span-5">
            <Eyebrow>Final Step</Eyebrow>
            <H2>Ready to operationalize your lab?</H2>
            <Lead className="mt-5">Our pricing is <span className="text-white font-bold">consultative</span> — based on your specific lab size, student volume, and infrastructure readiness. Submit a few details and a partnerships lead will respond within 48 hours.</Lead>
            <div className="mt-8 space-y-3">
              {["Personalized roadmap aligned to your phase", "Founder-Era pricing eligibility check", "Free 42-point ergo + safety audit estimate"].map((b, i) => (
                <div key={i} className="flex items-center gap-3 text-sm text-[#D4D4D8]">
                  <CheckCircle2 className="w-5 h-5 text-[#00FFD0]" /> {b}
                </div>
              ))}
            </div>
          </div>
          <div className="md:col-span-7">
            <form onSubmit={onSubmit} data-testid="proposal-form" className="relative rounded-2xl border border-white/8 bg-[#0C0C10] p-6 md:p-8 space-y-5 overflow-hidden">
              <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[#FF8C00]/10 blur-3xl pointer-events-none" />
              <div className="relative space-y-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-mono-tech uppercase tracking-widest text-[#FF8C00]">Get a Proposal</p>
                    <h3 className="mt-1 font-display font-black text-xl text-white">Tell us about your lab</h3>
                  </div>
                  <span className="text-[10px] font-mono-tech text-[#6a6a70] uppercase tracking-widest">Step 1 / 1</span>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="School Name *" name="organization" value={form.organization} onChange={onChange} required />
                  <Field label="Location (City, State) *" name="region" value={form.region} onChange={onChange} required />
                  <Field label="Executive Sponsor — Full Name *" name="full_name" value={form.full_name} onChange={onChange} required />
                  <Field label="Designation *" name="role" value={form.role} onChange={onChange} required placeholder="Principal / Director / Trustee" />
                  <Field label="Email *" type="email" name="email" value={form.email} onChange={onChange} required />
                  <Field label="Phone" name="phone" value={form.phone} onChange={onChange} />
                </div>
                <Select label="Current Lab Hardware Status *" name="hardware_status" value={form.hardware_status} onChange={onChange} options={HARDWARE_OPTIONS} required />
                <Select label="Estimated Student Volume *" name="student_volume" value={form.student_volume} onChange={onChange} options={VOLUME_OPTIONS} required />
                <div>
                  <label className="text-xs uppercase tracking-widest text-[#A1A1AA] block mb-2">Notes (Optional)</label>
                  <textarea name="message" rows={4} value={form.message} onChange={onChange} data-testid="input-message"
                    placeholder="Timeline, budget cycle, board priorities..."
                    className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder:text-[#6a6a70] focus:outline-none focus:border-[#FF8C00] text-sm resize-none" />
                </div>
                <button type="submit" disabled={loading} data-testid="submit-proposal"
                  className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-[#FF8C00] text-[#0A0A0C] font-black text-base uppercase tracking-widest hover:bg-[#FFA333] hover:shadow-[0_0_40px_rgba(255,140,0,0.55)] disabled:opacity-60 transition-all">
                  {loading ? "Submitting..." : "Get a Proposal"} <ArrowUpRight className="w-5 h-5" />
                </button>
              </div>
            </form>
          </div>
        </div>
      </Section>
    </div>
  );
}

function Field({ label, name, type = "text", value, onChange, required, placeholder }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-[#A1A1AA] block mb-2">{label}</label>
      <input type={type} name={name} value={value} onChange={onChange} required={required} placeholder={placeholder}
        data-testid={`input-${name}`}
        className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder:text-[#6a6a70] focus:outline-none focus:border-[#FF8C00] text-sm" />
    </div>
  );
}

function Select({ label, name, value, onChange, options, required }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-[#A1A1AA] block mb-2">{label}</label>
      <select name={name} value={value} onChange={onChange} required={required} data-testid={`input-${name}`}
        className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white focus:outline-none focus:border-[#FF8C00] text-sm appearance-none"
        style={{ backgroundImage: "url(\"data:image/svg+xml;charset=US-ASCII,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='%23FF8C00' d='M6 8L0 0h12z'/%3E%3C/svg%3E\")", backgroundRepeat: "no-repeat", backgroundPosition: "right 1rem center" }}>
        <option value="" disabled>Select an option</option>
        {options.map((o) => <option key={o} value={o} className="bg-[#0C0C10]">{o}</option>)}
      </select>
    </div>
  );
}
